/**
 * Lukemat — the map's own data, asked new questions. A tab in the map's
 * drawer: branch heat, the hottest chains, edge polarity, who wrote where,
 * hot leaves, deep surprises, contested nodes, second parents. Every row
 * that names nodes lights them on the map (onFocusNodes), so a reading is
 * a place to go, not a number to admire.
 *
 * Reads the readings endpoint (API_ENDPOINTS.IMPACT_PATH_READINGS, mapReadingsService).
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Flame, Route, GitFork, Users, Leaf, Sparkles, Scale, GitMerge, RefreshCw } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const Content = styled.div`min-width: 0; padding: 14px 14px 24px; display: flex; flex-direction: column; gap: 18px; color: #e5e7eb;`;
const Section = styled.section`display: flex; flex-direction: column; gap: 6px;`;
const H = styled.h3`margin: 0; display: flex; align-items: center; gap: 8px; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #9ca3af; svg { opacity: 0.8; }`;
const Hint = styled.p`margin: 0; font-size: 0.76rem; color: #6b7280; line-height: 1.4;`;
const Totals = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;
  div { background: #1a1a26; border: 1px solid #2f2f40; border-radius: 8px; padding: 8px 10px; }
  b { display: block; font-size: 1.1rem; font-weight: 700; color: #e8e8ef; font-variant-numeric: tabular-nums; }
  span { font-size: 0.68rem; color: #9ca3af; letter-spacing: 0.04em; text-transform: uppercase; }
`;
const Row = styled.button`
  display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: center; width: 100%; text-align: left;
  padding: 6px 8px; border-radius: 8px; border: 1px solid transparent; background: transparent; color: #e5e7eb; font: inherit; font-size: 0.82rem; line-height: 1.3; cursor: pointer;
  &:hover { background: rgba(255,255,255,0.05); border-color: #2f2f40; }
  &:focus-visible { outline: 1px solid #a78bfa; }
  .t { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sub { display: block; font-size: 0.72rem; color: #9ca3af; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .n { font-variant-numeric: tabular-nums; font-size: 0.78rem; color: #c9cbd6; white-space: nowrap; }
  .n b { color: #e8e8ef; }
`;
const Bar = styled.span`
  display: block; height: 4px; border-radius: 2px; background: #2f2f40; margin-top: 3px; overflow: hidden;
  i { display: block; height: 100%; width: ${(p) => Math.max(2, Math.min(100, p.$pct))}%; background: ${(p) => p.$color || '#a78bfa'}; }
`;
const Split = styled.span`
  display: inline-flex; height: 6px; width: 64px; border-radius: 3px; overflow: hidden; vertical-align: middle; margin-left: 6px;
  i { display: block; height: 100%; } .u { background: #3ecf8e; } .d { background: #e0708a; }
`;
const Polarity = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px;
  span { font-size: 0.72rem; padding: 2px 8px; border-radius: 999px; background: #1a1a26; border: 1px solid #2f2f40; color: #c9cbd6; font-variant-numeric: tabular-nums; }
  span.neg { border-color: rgba(224,112,138,0.5); color: #e0708a; }
`;
const Chain = styled.button`
  display: block; width: 100%; text-align: left; padding: 8px 10px; border-radius: 8px; border: 1px solid #2f2f40; background: #1a1a26; color: #e5e7eb; font: inherit; font-size: 0.78rem; line-height: 1.45; cursor: pointer;
  &:hover { border-color: #4a4a66; } &:focus-visible { outline: 1px solid #a78bfa; }
  .head { display: flex; justify-content: space-between; font-size: 0.7rem; color: #9ca3af; margin-bottom: 4px; b { color: #e8e8ef; } }
  .step { color: #c9cbd6; } .step b { color: #e8e8ef; font-weight: 600; } .arrow { color: #6b7280; }
`;
const Empty = styled.p`margin: 0; font-size: 0.78rem; color: #6b7280;`;
const Reload = styled.button`
  align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.14); background: transparent; color: #9ca3af; font: inherit; font-size: 0.74rem; cursor: pointer;
  &:hover { color: #e8e8ef; background: rgba(255,255,255,0.05); }
`;

const NEG = new Set(['weakens', 'blocks', 'nullifies', 'delays']);
const FI = { leads_to: 'johtaa', enables: 'mahdollistaa', reinforces: 'vahvistaa', weakens: 'heikentää', blocks: 'estää', nullifies: 'kumoaa', delays: 'viivyttää', requires: 'edellyttää', relates_to: 'liittyy', is_same: 'sama asia' };

export default function MapReadings({ instanceId, onFocusNodes, data: given = null }) {
  const [data, setData] = useState(given);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const load = async () => {
    if (!instanceId) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(API_ENDPOINTS.IMPACT_PATH_READINGS(instanceId));
      if (!r.ok) throw new Error(`${r.status}`);
      setData(await r.json());
    } catch (e) { setError(`Lukemien haku epäonnistui: ${e.message}`); } finally { setBusy(false); }
  };
  useEffect(() => { if (!given) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [instanceId, given]);

  if (error) return <Content><Empty>{error}</Empty><Reload type="button" onClick={load}><RefreshCw size={12} /> Yritä uudestaan</Reload></Content>;
  if (!data) return <Content><Empty>{busy ? 'Luetaan karttaa…' : 'Ei lukemia.'}</Empty></Content>;

  const t = data.totals || {};
  const go = (ids) => onFocusNodes?.(ids.filter(Boolean));
  const maxBranch = Math.max(1, ...(data.branches || []).map((b) => b.upPerNode));

  return (
    <Content>
      <Totals>
        <div><b>{t.consequences}</b><span>seurausta</span></div>
        <div><b>{t.up}</b><span>peukkua ylös</span></div>
        <div><b>{t.voted}</b><span>solmua äänestetty</span></div>
        <div><b>{t.upPerNode}</b><span>ylös / solmu</span></div>
        <div><b>{t.leafShare} %</b><span>lehtiä</span></div>
        <div><b>{t.grounded}</b><span>lähteistettyä</span></div>
      </Totals>

      <Section>
        <H><Flame size={14} /> Haarojen kuumuus</H>
        <Hint>Peukkuja solmua kohti muuttujan alla. Kartan keskiarvo {t.upPerNode}. Pieni kuuma haara on se, mihin huone palaa.</Hint>
        {(data.branches || []).slice(0, 10).map((b) => (
          <Row key={b.id} type="button" onClick={() => go([b.id])} title={`${b.nodes} solmua · ${b.writers} kirjoittajaa · ${b.grounded} lähteistettyä · syvyys ${b.maxDepth}`}>
            <span className="t">{b.title}<span className="sub">{b.scenarioName} · {b.nodes} solmua · {b.writers} kirj.</span><Bar $pct={(100 * b.upPerNode) / maxBranch} $color="#f0b35e"><i /></Bar></span>
            <span className="n"><b>{b.upPerNode}</b> / solmu</span>
          </Row>
        ))}
      </Section>

      <Section>
        <H><Route size={14} /> Kuumimmat ketjut</H>
        <Hint>Peukut summattuna juuresta lehteen. Tarina, johon huone uskoo eniten; leikkaa ryhmien ja haarojen poikki.</Hint>
        {(data.chains || []).slice(0, 3).map((c, i) => (
          <Chain key={i} type="button" onClick={() => go(c.nodes.map((n) => n.id))}>
            <div className="head"><span>{c.scenarioName} · {c.depth} askelta</span><span><b>+{c.up}</b></span></div>
            {c.nodes.map((n, j) => (
              <span key={n.id} className="step">{j > 0 && <span className="arrow"> → </span>}{n.up >= 10 ? <b>{n.text}</b> : n.text}</span>
            ))}
          </Chain>
        ))}
        {!(data.chains || []).length && <Empty>Ei vielä ketjuja.</Empty>}
      </Section>

      <Section>
        <H><GitFork size={14} /> Nuolien napaisuus</H>
        <Hint>{data.polarity?.negativeShare} % nuolista on heikentäviä tai estäviä. Ilman niitä kartalla ei voi olla tasapainottavia kierteitä.</Hint>
        <Polarity>
          {Object.entries(data.polarity?.counts || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <span key={k} className={NEG.has(k) ? 'neg' : ''}>{FI[k] || k} {v}</span>
          ))}
        </Polarity>
      </Section>

      <Section>
        <H><Users size={14} /> Kuka kirjoitti minne</H>
        <Hint>Keskimäärin {data.authorship?.meanWritersPerBranch} kirjoittajaa haaraa kohti, {t.writers} kirjoittajaa kartalla.</Hint>
        {(data.authorship?.monologues || []).length ? (
          <>
            <Hint>Yhden kirjoittajan haarat (monologi, ei keskustelu):</Hint>
            {data.authorship.monologues.map((m) => (
              <Row key={m.id} type="button" onClick={() => go([m.id])}><span className="t">{m.title}</span><span className="n">{m.nodes} solmua</span></Row>
            ))}
          </>
        ) : <Empty>Jokaisella isommalla haaralla on useampi kirjoittaja.</Empty>}
      </Section>

      <Section>
        <H><Sparkles size={14} /> Yllätykset syvällä</H>
        <Hint>Paljon peukkuja syvällä solmulla, vanhemmalla vähän: löytö hiljaisesta polusta.</Hint>
        {(data.surprises || []).map((n) => (
          <Row key={n.id} type="button" onClick={() => go([n.id])}>
            <span className="t">{n.text}<span className="sub">syvyys {n.depth} · alla: {n.parentText} (+{n.parentUp})</span></span>
            <span className="n"><b>+{n.up}</b></span>
          </Row>
        ))}
        {!(data.surprises || []).length && <Empty>Ei yllätyksiä vielä.</Empty>}
      </Section>

      <Section>
        <H><Scale size={14} /> Kiistanalaiset</H>
        <Hint>Peukkuja ylös ja alas molempia paljon: huone on eri mieltä siitä, onko tällä väliä. Seinäkeskustelun paikka.</Hint>
        {(data.contested || []).map((n) => (
          <Row key={n.id} type="button" onClick={() => go([n.id])}>
            <span className="t">{n.text}<span className="sub">{n.scenarioName}</span></span>
            <span className="n">+{n.up} −{n.down}<Split><i className="u" style={{ width: `${(100 * n.up) / (n.up + n.down)}%` }} /><i className="d" style={{ width: `${(100 * n.down) / (n.up + n.down)}%` }} /></Split></span>
          </Row>
        ))}
        {!(data.contested || []).length && <Empty>Ei kiistanalaisia solmuja.</Empty>}
      </Section>

      <Section>
        <H><Leaf size={14} /> Kuumat lehdet</H>
        <Hint>Lehti, jolla on paljon peukkuja: tähän huone lopetti ajattelun. Seuraavan kierroksen ”mitä tästä seuraa”.</Hint>
        {(data.hotLeaves || []).map((n) => (
          <Row key={n.id} type="button" onClick={() => go([n.id])}>
            <span className="t">{n.text}<span className="sub">{n.scenarioName} · syvyys {n.depth}</span></span>
            <span className="n"><b>+{n.up}</b></span>
          </Row>
        ))}
      </Section>

      <Section>
        <H><GitMerge size={14} /> Kaksi vanhempaa</H>
        <Hint>Solmu, joka seuraa kahdesta paikasta: huoneen vahvin yhteneväisyys.</Hint>
        {(data.multiParent || []).slice(0, 8).map((n) => (
          <Row key={n.id} type="button" onClick={() => go([n.id])}><span className="t">{n.text}</span><span className="n">{n.parents} vanhempaa</span></Row>
        ))}
        {!(data.multiParent || []).length && <Empty>Ei vielä.</Empty>}
      </Section>

      {data.writing && (
        <Hint>Kartta kirjoitettiin {new Date(data.writing.first).toLocaleString('fi-FI', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })} – {new Date(data.writing.last).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}, {data.writing.nodes} solmua.</Hint>
      )}
      <Reload type="button" onClick={load} disabled={busy}><RefreshCw size={12} /> {busy ? 'Luetaan…' : 'Lue uudestaan'}</Reload>
    </Content>
  );
}
